import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Link2 } from 'lucide-react';
import ReusableDataTable from '../../components/table/ReusableDataTable';
import { getZellePaymentsList } from '../../store/thunk/adminThunk';
import { formatPrice, formatToUSDate } from '../../hooks/commonFunction';
import toast from 'react-hot-toast';
import useIsMobile from '../../hooks/useIsMobile';
import OrderMappingModal from '../../components/admin/OrderMappingModal';

const ZellePaymentList = () => {
  const dispatch = useDispatch();
  const { coreSettings } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();

  // State management
  const [zellePayments, setZellePayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [selectedZellePayment, setSelectedZellePayment] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Handle open mapping modal
  const handleOpenMappingModal = (row) => {
    setSelectedZellePayment(row);
    setIsMappingModalOpen(true);
  };

  // Handle apply mappings
  const handleApplyMappings = (mappedOrders) => {
    console.log('Mapped orders for Zelle Payment:', selectedZellePayment?.id, mappedOrders);
    // TODO: Implement API call to save the mappings
    toast.success(`Successfully mapped ${mappedOrders.length} order(s)`);
    // Optionally refresh the list
    fetchZellePaymentsList(currentPage, rowsPerPage);
  };

  // Define columns
  const columns = [
    {
      name: 'ID',
      selector: (row) => row.id,
      sortable: true,
      width: '70px',
    },
    {
      name: 'Name',
      cell: (row) => <span className='font-medium'>{row.name || 'N/A'}</span>,
      sortable: true,
      width: '150px',
    },
    {
      name: 'Payment ID',
      cell: (row) => <span className='font-mono text-sm'>#{row.payment_id || 'N/A'}</span>,
      sortable: true,
      width: '110px',
    },
    {
      name: 'Amount',
      cell: (row) => <span className='font-medium'>{formatPrice(row.amount)}</span>,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Status',
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
            row.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : row.status === 'completed'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status || 'pending'}
        </span>
      ),
      sortable: true,
    },
    {
      name: 'Transaction ID',
      cell: (row) => <span className='font-mono text-xs'>{row.transaction_id || 'N/A'}</span>,
      sortable: true,
    },
    {
      name: 'Transaction Date',
      cell: (row) => formatToUSDate(row.transaction_date),
      sortable: true,
    },
    {
      name: 'Memo',
      cell: (row) => (
        <div className='text-xs max-w-xs truncate' title={row.memo}>
          {row.memo || 'N/A'}
        </div>
      ),
      sortable: false,
    },
    // {
    //   name: 'Error Message',
    //   cell: (row) => (
    //     <div className='text-xs text-red-600 max-w-xs truncate' title={row.error_message}>
    //       {row.error_message || '-'}
    //     </div>
    //   ),
    //   sortable: false,
    //   width: '150px',
    // },
    {
      name: 'Created At',
      cell: (row) => formatToUSDate(row.created_at),
      sortable: true,
    },
    {
      name: 'Mappings',
      cell: (row) => (
        <button
          onClick={() => handleOpenMappingModal(row)}
          disabled={row.status === 'processed'}
          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
            row.status === 'processed'
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
              : 'bg-primary-100 text-white hover:bg-primary-200'
          }`}
          title={row.status === 'processed' ? 'Already processed' : 'Map orders to this Zelle payment'}
        >
          <Link2 size={14} />
          {row.status === 'processed' ? 'Processed' : 'Mapping'}
        </button>
      ),
      width: '120px',
    },
  ];

  // Fetch Zelle payments list
  const fetchZellePaymentsList = async (page = 1, limit = 10) => {
    try {
      setIsLoading(true);

      const params = {
        page,
        limit,
      };

      const result = await dispatch(getZellePaymentsList(params)).unwrap();

      if (result?.status === 1 && result?.data) {
        let filteredData = result.data.zellePayments || result.data.payments || result.data || [];

        // Apply search filter
        if (searchQuery) {
          const searchLower = searchQuery.toLowerCase();
          filteredData = filteredData.filter((item) => {
            return (
              item.name?.toLowerCase().includes(searchLower) ||
              item.payment_id?.toString().includes(searchLower) ||
              item.transaction_id?.toString().toLowerCase().includes(searchLower) ||
              item.amount?.toString().includes(searchLower) ||
              item.status?.toLowerCase().includes(searchLower) ||
              item.memo?.toLowerCase().includes(searchLower) ||
              item.error_message?.toLowerCase().includes(searchLower)
            );
          });
        }

        setZellePayments(filteredData);
        setTotalRows(result.data.pagination?.total || filteredData.length);
        setCurrentPage(result.data.pagination?.page || page);
      }
    } catch (error) {
      console.error('Failed to fetch Zelle payments list:', error);
      toast.error('Failed to fetch Zelle payments list');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchZellePaymentsList(currentPage, rowsPerPage);
  }, []);

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchZellePaymentsList(1, rowsPerPage);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchZellePaymentsList(page, rowsPerPage);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
    fetchZellePaymentsList(page, newPerPage);
  };

  return (
    <section className='overflow-y-auto relative no-scrollbar'>
      <div className='container px-0'>
        <div className='p-4'>
          <div className='py-5 bg-neutral-100 px-5 mb-5 rounded-lg'>
            {/* Header Section */}
            <div className='flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
              <h2 className='text-xl font-semibold'>Zelle Payment List</h2>

              {/* Search Controls */}
              <div className='flex gap-2 items-center'>
                {/* Search */}
                <div className='relative flex-1 md:w-60'>
                  <input
                    type='text'
                    placeholder='Search by name, payment ID, transaction ID...'
                    value={searchQuery}
                    onChange={handleSearch}
                    className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-sm pr-2 pl-10 outline-none focus:ring-2 focus:ring-red-700 focus:border-transparent hover:border-gray-400 transition-colors w-full'
                  />
                  <Search className='absolute top-1/2 left-4 -translate-y-1/2 h-4 w-4 text-primary-500' />
                </div>
              </div>
            </div>
          </div>

          <ReusableDataTable
            columns={columns}
            data={zellePayments}
            isLoading={isLoading}
            showCheckbox={false}
            showDateRangeFilter={false}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        </div>
      </div>

      {/* Order Mapping Modal */}
      <OrderMappingModal
        open={isMappingModalOpen}
        setOpen={setIsMappingModalOpen}
        onApply={handleApplyMappings}
        zellePaymentDetails={selectedZellePayment}
      />
    </section>
  );
};

export default ZellePaymentList;
